import React, { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Code,
  Group,
  Modal,
  NumberInput,
  Paper,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useLoaderData, useRevalidator } from 'react-router-dom';
import { z } from 'zod';
import {
  ApiActivityPageSchema,
  ApiAdminEventsPageSchema,
  ApiKeySchema,
  type ApiActivity,
  type ApiAdminEvent,
  type ApiKey,
  type ApiKeysPage as PageData,
  type ApiKeyScope,
} from '@tinypro-audit/contracts';
import { fetchJson } from '../api/client';

const base = '/api/admin/api-keys';
const permissions: { value: ApiKeyScope; label: string }[] = [
  {
    value: 'evidence:write',
    label: 'Upload evidence and create collection batches',
  },
  { value: 'status:read', label: 'Read project status and evidence inventory' },
  { value: 'results:read', label: 'Read analysis results and export CSV' },
];
const date = (value: string | null) =>
  value ? new Date(value).toLocaleString() : '—';
const localDate = (value: string | null) =>
  value
    ? new Date(
        new Date(value).getTime() - new Date(value).getTimezoneOffset() * 60000,
      )
        .toISOString()
        .slice(0, 16)
    : '';

export function ApiKeysPage(): React.ReactElement {
  const data = useLoaderData() as PageData;
  const revalidator = useRevalidator();
  const [tab, setTab] = useState<string | null>('keys');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<ApiKey | 'new' | null>(null);
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<ApiKeyScope[]>(
    permissions.map((p) => p.value),
  );
  const [expires, setExpires] = useState('');
  const [confirm, setConfirm] = useState<{
    key: ApiKey;
    action: 'rotate' | 'revoke';
  } | null>(null);
  const [secret, setSecret] = useState<{ value: string; name: string } | null>(
    null,
  );
  const [retention, setRetention] = useState<number | string>(
    data.retentionDays,
  );
  const [retentionConfirm, setRetentionConfirm] = useState(false);
  const [keyFilter, setKeyFilter] = useState<string | null>(null);
  const [projectFilter, setProjectFilter] = useState('');
  const [requestFilter, setRequestFilter] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [outcome, setOutcome] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [history, setHistory] = useState<{
    items: (ApiActivity | ApiAdminEvent)[];
    total: number;
  }>({ items: [], total: 0 });
  const [query, setQuery] = useState('');
  const [detail, setDetail] = useState<ApiActivity | ApiAdminEvent | null>(
    null,
  );

  useEffect(() => {
    if (!secret) return;
    const timer = window.setTimeout(() => setSecret(null), 60000);
    const hide = () => {
      if (document.hidden) setSecret(null);
    };
    document.addEventListener('visibilitychange', hide);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', hide);
    };
  }, [secret]);
  useEffect(() => {
    if (tab === 'keys') return;
    let active = true;
    setError('');
    const endpoint = tab === 'activity' ? 'activity' : 'events';
    const schema =
      tab === 'activity' ? ApiActivityPageSchema : ApiAdminEventsPageSchema;
    // Both history responses share the same envelope; the selected schema validates each row.
    fetchJson(
      `${base}/${endpoint}?${query}&offset=${offset}`,
      { method: 'GET' },
      schema as z.ZodType<typeof history>,
    )
      .then((value) => {
        if (active) setHistory(value);
      })
      .catch((err) => {
        if (active) setError(err.message);
      });
    return () => {
      active = false;
    };
  }, [tab, query, offset]);

  const act = async (operation: () => Promise<unknown>, message: string) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await operation();
      setNotice(message);
      await revalidator.revalidate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed.');
    } finally {
      setBusy(false);
    }
  };
  const openEditor = (key: ApiKey | 'new') => {
    setEditing(key);
    setName(key === 'new' ? '' : key.name);
    setScopes(key === 'new' ? permissions.map((p) => p.value) : key.scopes);
    setExpires(key === 'new' ? '' : localDate(key.expiresAt));
  };
  const save = () =>
    act(async () => {
      await fetchJson(
        editing === 'new' ? base : `${base}/${(editing as ApiKey).id}`,
        {
          method: editing === 'new' ? 'POST' : 'PATCH',
          body: JSON.stringify({
            name,
            scopes,
            expiresAt: expires ? new Date(expires).toISOString() : null,
          }),
        },
        ApiKeySchema,
      );
      setEditing(null);
    }, 'Key saved. Use Reveal to retrieve the secret; each reveal is recorded.');
  const reveal = (key: ApiKey) =>
    act(async () => {
      const value = await fetchJson(
        `${base}/${key.id}/reveal`,
        { method: 'POST', body: '{}' },
        z.object({ secret: z.string() }),
      );
      setSecret({ value: value.secret, name: key.name });
    }, 'Secret reveal recorded in the administrative audit trail.');
  const applyFilters = () => {
    try {
      const values = new URLSearchParams();
      if (keyFilter) values.set('keyId', keyFilter);
      if (tab === 'activity' && projectFilter)
        values.set('projectId', projectFilter);
      if (tab === 'activity' && outcome) values.set('outcome', outcome);
      if (requestFilter) values.set('requestId', requestFilter);
      if (from) values.set('from', new Date(from).toISOString());
      if (to) values.set('to', new Date(to).toISOString());
      setOffset(0);
      setQuery(values.toString());
    } catch {
      setError('Enter valid dates.');
    }
  };

  return (
    <Stack>
      <Group justify="space-between">
        <div>
          <Title order={2}>API keys</Title>
          <Text c="dimmed">
            {data.org.name} · ServiceTitan tenant {data.org.tenant}
          </Text>
        </div>
        <Button
          disabled={!data.encryptionConfigured}
          onClick={() => openEditor('new')}
        >
          Create key
        </Button>
      </Group>
      {!data.encryptionConfigured && (
        <Alert color="yellow">
          Secret encryption is not configured. Configure the server encryption
          key before creating or revealing API keys.
        </Alert>
      )}
      {error && (
        <Alert color="red" role="alert">
          {error}
        </Alert>
      )}
      {notice && <Alert color="green">{notice}</Alert>}
      <Tabs
        value={tab}
        onChange={(value) => {
          setTab(value);
          setOffset(0);
          setQuery('');
          setHistory({ items: [], total: 0 });
          setDetail(null);
        }}
      >
        <Tabs.List>
          <Tabs.Tab value="keys">Keys</Tabs.Tab>
          <Tabs.Tab value="activity">API activity</Tabs.Tab>
          <Tabs.Tab value="events">Administrative audit trail</Tabs.Tab>
        </Tabs.List>
      </Tabs>
      {tab === 'keys' ? (
        <>
          <Paper withBorder p="md">
            <Text size="sm">
              Keys are bound to this organization and tenant. Secrets are
              encrypted separately from their verification hashes. Rotation
              replaces a secret immediately; revocation permanently disables it.
            </Text>
          </Paper>
          <Table.ScrollContainer minWidth={900}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name / key</Table.Th>
                  <Table.Th>Permissions</Table.Th>
                  <Table.Th>Status / expiration</Table.Th>
                  <Table.Th>Last called</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.items.map((key) => (
                  <Table.Tr key={key.id}>
                    <Table.Td>
                      <Text fw={600}>{key.name}</Text>
                      <Code>{key.maskedToken}</Code>
                      <Text size="xs" c="dimmed">
                        Version {key.version}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {key.scopes.map((scope) => (
                        <Text size="sm" key={scope}>
                          {scope}
                        </Text>
                      ))}
                    </Table.Td>
                    <Table.Td>
                      <Badge color={key.status === 'active' ? 'green' : 'gray'}>
                        {key.status}
                      </Badge>
                      <Text size="xs">
                        {key.expiresAt ? date(key.expiresAt) : 'No expiration'}
                      </Text>
                    </Table.Td>
                    <Table.Td>{date(key.lastCalledAt)}</Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <Button
                          size="xs"
                          variant="light"
                          disabled={busy || key.status === 'revoked'}
                          onClick={() => openEditor(key)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="xs"
                          variant="light"
                          disabled={
                            busy ||
                            key.status !== 'active' ||
                            !data.encryptionConfigured
                          }
                          onClick={() => void reveal(key)}
                        >
                          Reveal
                        </Button>
                        <Button
                          size="xs"
                          variant="light"
                          disabled={
                            busy ||
                            key.status === 'revoked' ||
                            !data.encryptionConfigured
                          }
                          onClick={() => setConfirm({ key, action: 'rotate' })}
                        >
                          Rotate
                        </Button>
                        <Button
                          size="xs"
                          color="red"
                          variant="subtle"
                          disabled={busy || key.status === 'revoked'}
                          onClick={() => setConfirm({ key, action: 'revoke' })}
                        >
                          Revoke
                        </Button>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          {!data.items.length && (
            <Text c="dimmed">
              No API keys yet. Create one for your evidence collector.
            </Text>
          )}
          <Paper withBorder p="md">
            <Stack gap="sm">
              <Title order={4}>Activity retention</Title>
              <Text size="sm">
                API request logs older than this period are deleted
                automatically. Administrative key events and project evidence
                are retained.
              </Text>
              <Group align="end">
                <NumberInput
                  label="Days to retain request logs"
                  min={1}
                  max={365}
                  value={retention}
                  onChange={setRetention}
                />
                <Button
                  disabled={
                    Number(retention) === data.retentionDays ||
                    !Number.isInteger(Number(retention)) ||
                    Number(retention) < 1 ||
                    Number(retention) > 365
                  }
                  onClick={() => setRetentionConfirm(true)}
                >
                  Save retention
                </Button>
              </Group>
            </Stack>
          </Paper>
        </>
      ) : (
        <>
          <Group align="end">
            <Select
              label="Key"
              clearable
              value={keyFilter}
              onChange={setKeyFilter}
              data={data.items.map((key) => ({
                value: key.id,
                label: key.name,
              }))}
            />
            {tab === 'activity' && (
              <>
                <TextInput
                  label="ServiceTitan project ID"
                  value={projectFilter}
                  onChange={(e) => setProjectFilter(e.currentTarget.value)}
                />
                <Select
                  label="Outcome"
                  clearable
                  data={['success', 'failure']}
                  value={outcome}
                  onChange={setOutcome}
                />
              </>
            )}
            <TextInput
              type="datetime-local"
              label="From"
              value={from}
              onChange={(e) => setFrom(e.currentTarget.value)}
            />
            <TextInput
              type="datetime-local"
              label="To"
              value={to}
              onChange={(e) => setTo(e.currentTarget.value)}
            />
            <TextInput
              label="Request ID"
              value={requestFilter}
              onChange={(e) => setRequestFilter(e.currentTarget.value)}
            />
            <Button onClick={applyFilters}>Search</Button>
          </Group>
          <Table.ScrollContainer minWidth={900}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Time</Table.Th>
                  <Table.Th>Key</Table.Th>
                  <Table.Th>
                    {tab === 'activity' ? 'Request' : 'Action / administrator'}
                  </Table.Th>
                  <Table.Th>
                    {tab === 'activity' ? 'Status / duration' : 'Request ID'}
                  </Table.Th>
                  <Table.Th>Details</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {history.items.map((item) => (
                  <Table.Tr
                    key={'startedAt' in item ? item.requestId : item.id}
                  >
                    <Table.Td>
                      {date(
                        'startedAt' in item ? item.startedAt : item.createdAt,
                      )}
                    </Table.Td>
                    <Table.Td>{item.keyName ?? '—'}</Table.Td>
                    {'startedAt' in item ? (
                      <>
                        <Table.Td>
                          {item.method} {item.endpoint}
                          <Text size="xs" c="dimmed">
                            Project {item.projectId ?? '—'}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            color={
                              item.httpStatus && item.httpStatus < 400
                                ? 'green'
                                : 'red'
                            }
                          >
                            {item.httpStatus ?? 'In progress'}
                          </Badge>
                          <Text size="xs">
                            {item.durationMs === null
                              ? '—'
                              : `${item.durationMs} ms`}
                          </Text>
                        </Table.Td>
                      </>
                    ) : (
                      <>
                        <Table.Td>
                          {item.action}
                          <Text size="xs">{item.actorEmail}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Code>{item.requestId}</Code>
                        </Table.Td>
                      </>
                    )}
                    <Table.Td>
                      <Button
                        size="xs"
                        variant="subtle"
                        onClick={() => setDetail(item)}
                      >
                        View
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          {!history.items.length && (
            <Text c="dimmed">No matching records.</Text>
          )}
          <Group justify="space-between">
            <Text size="sm">{history.total} records</Text>
            <Group>
              <Button
                variant="default"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - 50))}
              >
                Previous
              </Button>
              <Button
                variant="default"
                disabled={offset + 50 >= history.total}
                onClick={() => setOffset(offset + 50)}
              >
                Next
              </Button>
            </Group>
          </Group>
        </>
      )}
      <Modal
        opened={editing !== null}
        onClose={() => !busy && setEditing(null)}
        title={editing === 'new' ? 'Create API key' : 'Edit API key'}
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <Stack>
            <Text size="sm">
              {data.org.name} · Tenant {data.org.tenant}
            </Text>
            <TextInput
              required
              maxLength={100}
              label="Name"
              value={name}
              onChange={(event) => setName(event.currentTarget.value)}
            />
            <Checkbox.Group
              label="Permissions"
              value={scopes}
              onChange={(values) => setScopes(values as ApiKeyScope[])}
            >
              <Stack gap="xs" mt="xs">
                {permissions.map((p) => (
                  <Checkbox key={p.value} value={p.value} label={p.label} />
                ))}
              </Stack>
            </Checkbox.Group>
            <TextInput
              type="datetime-local"
              label="Expiration (your local time)"
              description="Leave empty for no expiration."
              value={expires}
              onChange={(event) => setExpires(event.currentTarget.value)}
            />
            <Button
              type="submit"
              loading={busy}
              disabled={!name.trim() || !scopes.length}
            >
              Save key
            </Button>
          </Stack>
        </form>
      </Modal>
      <Modal
        opened={confirm !== null}
        onClose={() => !busy && setConfirm(null)}
        title={`${confirm?.action === 'rotate' ? 'Rotate' : 'Revoke'} ${confirm?.key.name ?? 'key'}`}
      >
        <Stack>
          <Text>
            {confirm?.action === 'rotate'
              ? 'The current secret will stop working immediately. Reveal the replacement and update your collector.'
              : 'This key will stop working immediately. Revoked keys cannot be reactivated.'}
          </Text>
          <Button
            color="red"
            loading={busy}
            onClick={() =>
              void act(async () => {
                if (!confirm) return;
                await fetchJson(
                  `${base}/${confirm.key.id}/${confirm.action}`,
                  { method: 'POST', body: '{}' },
                  ApiKeySchema,
                );
                setConfirm(null);
              }, 'Key updated.')
            }
          >
            Confirm {confirm?.action}
          </Button>
        </Stack>
      </Modal>
      <Modal
        opened={secret !== null}
        onClose={() => setSecret(null)}
        title={`Secret: ${secret?.name ?? ''}`}
      >
        <Stack>
          <Text size="sm">
            This reveal was audited. The secret is hidden after 60 seconds or
            when you leave this tab. Store it in your collector’s secret
            manager.
          </Text>
          <Code
            block
            style={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}
          >
            {secret?.value}
          </Code>
          <Button
            onClick={() => {
              if (secret)
                void navigator.clipboard
                  .writeText(secret.value)
                  .then(() => setNotice('Secret copied.'))
                  .catch(() =>
                    setError(
                      'Clipboard unavailable. Select the secret and copy it manually.',
                    ),
                  );
            }}
          >
            Copy secret
          </Button>
          <Button variant="default" onClick={() => setSecret(null)}>
            Hide
          </Button>
        </Stack>
      </Modal>
      <Modal
        opened={detail !== null}
        onClose={() => setDetail(null)}
        title="Record details"
        size="lg"
      >
        <Text size="sm" mb="sm">
          Credentials and uploaded file contents are excluded from activity
          records.
        </Text>
        <Code
          block
          style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
        >
          {JSON.stringify(detail, null, 2)}
        </Code>
      </Modal>
      <Modal
        opened={retentionConfirm}
        onClose={() => !busy && setRetentionConfirm(false)}
        title="Change request log retention"
      >
        <Stack>
          <Text>
            Keep request logs for {retention} days. Logs older than this will be
            permanently removed by scheduled cleanup.
          </Text>
          <Button
            loading={busy}
            onClick={() =>
              void act(async () => {
                await fetchJson(
                  `${base}/retention`,
                  {
                    method: 'PUT',
                    body: JSON.stringify({ days: Number(retention) }),
                  },
                  z.object({ days: z.number() }),
                );
                setRetentionConfirm(false);
              }, 'Retention updated.')
            }
          >
            Confirm retention
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
}
